/**
 * Group-thread READ routing flag (group-threading P3c, Option B).
 *
 * Gates the client read of the shared `group_thread_messages/{threadId}` store
 * (the overlay merged into thread-open reads, and the P3d sender attribution).
 *
 * ACCOUNT-SCOPED, matching the sibling group-threading flags
 * (threadAddPeople / threadMentions / multi-choice panel). PLATFORM DEFAULT ON
 * (owner directive 2026-08-17, completing the all-accounts rollout approved
 * 2026-07-24): `GROUP_THREAD_READ_ENABLED` is consulted AFTER the blank-account
 * guard — it acts like a '*' allowlist token, never a bypass, so anonymous /
 * not-yet-resolved sessions stay fail-closed. Rollback: flip the constant to
 * false and the `REACT_APP_GROUP_THREAD_READ_ACCOUNTS` CSV governs again.
 *
 * The overlay read happens deep in the repository layer, which has no accountId
 * in scope, so the active account is published once by the chat mount via
 * `setGroupThreadReadAccount` and used as the default when a caller doesn't pass
 * one. The render path (which has the accountId) passes it explicitly.
 *
 * Even with the flag on, the real data containment is server-side: firestore
 * rules only admit thread PARTICIPANTS, so a non-participant read is denied and
 * swallowed regardless of any client flag state.
 */
export const GROUP_THREAD_READ_ENABLED: boolean = true;

export const GROUP_THREAD_READ_ACCOUNTS_ENV = 'REACT_APP_GROUP_THREAD_READ_ACCOUNTS';

// Active account published by the chat mount for the repository-layer reads.
let activeAccountId: string | null = null;
export function setGroupThreadReadAccount(accountId: string | null | undefined): void {
  activeAccountId = (accountId ?? '').trim() || null;
}

function readEnabledReadAccountsFromEnv(env?: Record<string, string | undefined>): Set<string> {
  const raw = (env
    ? env[GROUP_THREAD_READ_ACCOUNTS_ENV]
    : process.env.REACT_APP_GROUP_THREAD_READ_ACCOUNTS) ?? '';
  return new Set(
    String(raw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

export interface GroupThreadReadFlagCtx {
  /** Account to gate on; defaults to the mount-published active account. */
  accountId?: string | null;
  /** Test/override seam: explicit allowlist instead of the env CSV. */
  enabledAccountIds?: Iterable<string>;
  /** Test seam: injected env map. Production uses the inlined literal. */
  env?: Record<string, string | undefined>;
  /** Test seam for the compile default (rollback-path tests). Production callers never pass this. */
  defaultEnabled?: boolean;
}

export function resolveGroupThreadReadEnabled(ctx: GroupThreadReadFlagCtx = {}): boolean {
  const accountId = (ctx.accountId ?? activeAccountId ?? '').trim();
  if (!accountId) return false;
  // Compile default AFTER the blank guard — the platform default can never
  // admit an unresolved session (unlike the pre-2026-08-17 early return).
  if (ctx.defaultEnabled ?? GROUP_THREAD_READ_ENABLED) return true;
  const source = ctx.enabledAccountIds ?? readEnabledReadAccountsFromEnv(ctx.env);
  const set = source instanceof Set ? source : new Set(source);
  // ALL-ACCOUNTS ROLLOUT (approved 2026-07-24): the literal '*' in the CSV
  // admits every RESOLVED account. Deliberately checked AFTER the blank-account
  // fail-closed guard above — unlike the compile-constant override, '*' never
  // enables group reads for anonymous / not-yet-resolved sessions. Rollback is
  // a repo-variable edit + frontend redeploy, no code revert.
  if (set.has('*')) return true;
  return set.has(accountId);
}

/**
 * Per-message seen marks — the one lever (2026-09-10; Miles 2026-09-09: "the
 * identifier that shows whom has viewed a message needs to be on all messages
 * … if they've had open to that particular message for more than 2 seconds").
 * Build-time: `REACT_APP_PER_MESSAGE_SEEN=off` stops the per-message writer,
 * makes the thread dwell write the high-water receipt again, and makes every
 * reader ignore the `seen` map — today's behaviour for everyone; anything else
 * = on. The rules keep accepting the optional map harmlessly either way.
 */
export function perMessageSeenEnabled(env: Record<string, string | undefined> = process.env): boolean {
  return String(env.REACT_APP_PER_MESSAGE_SEEN ?? '').trim().toLowerCase() !== 'off';
}

export const TYPING_PRESENCE_ACCOUNTS_ENV = 'REACT_APP_TYPING_PRESENCE_ACCOUNTS';

function readEnabledTypingAccountsFromEnv(env?: Record<string, string | undefined>): Set<string> {
  const raw = (env
    ? env[TYPING_PRESENCE_ACCOUNTS_ENV]
    : process.env.REACT_APP_TYPING_PRESENCE_ACCOUNTS) ?? '';
  return new Set(
    String(raw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

/**
 * TYPING PRESENCE in group threads — the one lever (T-R15, Miles 2026-09-21:
 * "there should also be a functionality to see when someone is typing … when
 * there's no changes to the draft box in 4 seconds it should not say [user
 * name] is responding").
 *
 * OFF by default, unlike the per-message-seen flag above: this is a NEW store
 * path, a new listener and a new write on every keystroke burst, so it ships
 * dark. Absent ⇒ today's behaviour byte for byte: the writer never publishes,
 * the reader never attaches, and no browser ever touches
 * `group_thread_messages/{t}/typing`.
 *
 * ACCOUNT-SCOPED, like every sibling client flag in this file's neighbourhood
 * (threadAddPeople / threadMentions / the summon gate / the read flag) — and
 * NOT the bare `=on` boolean it shipped as on 2026-09-22. Two reasons, both
 * learned rather than assumed:
 *
 *   1. A boolean can only be fired at the WHOLE FLEET. `GROUP_THREAD_READ_ENABLED`
 *      is a platform default-on, so `=on` would have switched a brand-new write
 *      path on for every account at once — exactly what the canary discipline
 *      (`off → canary → live`, the shape `ops/groupThreads` already uses for the
 *      live-turn mirror and the fold-others door) exists to prevent. A lever you
 *      cannot canary is not a usable lever for a new store path.
 *   2. The CSV gives BOTH: a canary (`prest0-inc,testing-account`) and the
 *      fleet (`*`), through the same token the read flag already honours.
 *
 * The blank-account guard is deliberately FIRST and has no compile-constant
 * override: an anonymous or not-yet-resolved session can never publish presence,
 * because there is nobody to name.
 *
 * The group-thread READ flag still gates everything above it — typing presence
 * is a fact of a shared room, and a session that cannot read the room has no
 * business reading who is typing in it.
 *
 * T-R1 is untouched and is a DIFFERENT ruling: the voice session's typing
 * presence stays private to the person in that session. This is the room's.
 */
export function resolveTypingPresenceEnabled(
  ctx: GroupThreadReadFlagCtx & { env?: Record<string, string | undefined> } = {},
): boolean {
  if (!resolveGroupThreadReadEnabled(ctx)) return false;
  const accountId = (ctx.accountId ?? activeAccountId ?? '').trim();
  if (!accountId) return false;
  const source = ctx.enabledAccountIds ?? readEnabledTypingAccountsFromEnv(ctx.env);
  const set = source instanceof Set ? source : new Set(source);
  if (set.has('*')) return true;
  return set.has(accountId);
}
